/**
 * Configuration Templates Panel
 * Provides pre-configured templates for different platforms and scenarios
 */

import React, { useState } from 'react';
import { useConfigStore } from '../stores/configStore';
import type { SystemConfig } from '../types/config';

interface ConfigTemplate {
  id: string;
  name: string;
  description: string;
  platform: string;
  scenario: string;
  config: Partial<SystemConfig>;
}

const CONFIGURATION_TEMPLATES: ConfigTemplate[] = [
  {
    id: 'ebs-production',
    name: 'EBS Production',
    description: 'Production configuration for Oracle EBS 12.x',
    platform: 'EBS',
    scenario: 'Production',
    config: {
      platform: {
        selectedPlatform: 'EBS',
        platformName: 'Oracle E-Business Suite',
        platformVersion: '12.2',
        platformDescription: 'Oracle EBS 12.x Production',
      },
      logging: {
        level: 'warn',
        format: 'json',
        maxFileSize: 500,
        maxFiles: 20,
        retentionDays: 90,
        enableConsoleLogging: false,
        enableFileLogging: true,
      },
      security: {
        jwtSecret: 'your-secret-key-min-32-characters-required',
        jwtExpiration: 24,
        passwordMinLength: 12,
        passwordRequireUppercase: true,
        passwordRequireLowercase: true,
        passwordRequireNumbers: true,
        passwordRequireSpecialChars: true,
        maxLoginAttempts: 3,
        lockoutDuration: 30,
        enableMFA: true,
      },
      api: {
        baseUrl: 'https://your-production-api.com',
        timeout: 60,
        retryAttempts: 5,
        retryDelay: 2,
        enableCORS: false,
        corsOrigins: [],
        rateLimitEnabled: true,
        rateLimitRequests: 1000,
        rateLimitWindow: 60,
      },
    },
  },
  {
    id: 'ebs-development',
    name: 'EBS Development',
    description: 'Development configuration for Oracle EBS 12.x',
    platform: 'EBS',
    scenario: 'Development',
    config: {
      platform: {
        selectedPlatform: 'EBS',
        platformName: 'Oracle E-Business Suite',
        platformVersion: '12.2',
        platformDescription: 'Oracle EBS 12.x Development',
      },
      logging: {
        level: 'debug',
        format: 'json',
        maxFileSize: 100,
        maxFiles: 5,
        retentionDays: 7,
        enableConsoleLogging: true,
        enableFileLogging: true,
      },
      security: {
        jwtSecret: 'dev-secret-key-min-32-characters-required',
        jwtExpiration: 48,
        passwordMinLength: 8,
        passwordRequireUppercase: true,
        passwordRequireLowercase: true,
        passwordRequireNumbers: true,
        passwordRequireSpecialChars: false,
        maxLoginAttempts: 10,
        lockoutDuration: 5,
        enableMFA: false,
      },
      api: {
        baseUrl: 'http://localhost:3000',
        timeout: 30,
        retryAttempts: 3,
        retryDelay: 1,
        enableCORS: true,
        corsOrigins: ['http://localhost:5173', 'http://localhost:3000'],
        rateLimitEnabled: false,
        rateLimitRequests: 0,
        rateLimitWindow: 0,
      },
    },
  },
  {
    id: 'fusion-production',
    name: 'Fusion Production',
    description: 'Production configuration for Oracle Fusion Cloud',
    platform: 'FUSION',
    scenario: 'Production',
    config: {
      platform: {
        selectedPlatform: 'FUSION',
        platformName: 'Oracle Fusion Cloud ERP',
        platformVersion: '23.x',
        platformDescription: 'Oracle Fusion Cloud Production',
      },
      logging: {
        level: 'warn',
        format: 'json',
        maxFileSize: 500,
        maxFiles: 20,
        retentionDays: 90,
        enableConsoleLogging: false,
        enableFileLogging: true,
      },
      security: {
        jwtSecret: 'your-secret-key-min-32-characters-required',
        jwtExpiration: 24,
        passwordMinLength: 12,
        passwordRequireUppercase: true,
        passwordRequireLowercase: true,
        passwordRequireNumbers: true,
        passwordRequireSpecialChars: true,
        maxLoginAttempts: 3,
        lockoutDuration: 30,
        enableMFA: true,
      },
    },
  },
  {
    id: 'netsuite-production',
    name: 'NetSuite Production',
    description: 'Production configuration for Oracle NetSuite',
    platform: 'NETSUITE',
    scenario: 'Production',
    config: {
      platform: {
        selectedPlatform: 'NETSUITE',
        platformName: 'Oracle NetSuite',
        platformVersion: '2024.1',
        platformDescription: 'Oracle NetSuite Production',
      },
      logging: {
        level: 'warn',
        format: 'json',
        maxFileSize: 500,
        maxFiles: 20,
        retentionDays: 90,
        enableConsoleLogging: false,
        enableFileLogging: true,
      },
    },
  },
  {
    id: 'jde-production',
    name: 'JDE Production',
    description: 'Production configuration for Oracle JD Edwards',
    platform: 'JDE',
    scenario: 'Production',
    config: {
      platform: {
        selectedPlatform: 'JDE',
        platformName: 'Oracle JD Edwards',
        platformVersion: '9.2',
        platformDescription: 'Oracle JDE Production',
      },
      logging: {
        level: 'warn',
        format: 'json',
        maxFileSize: 500,
        maxFiles: 20,
        retentionDays: 90,
        enableConsoleLogging: false,
        enableFileLogging: true,
      },
    },
  },
  {
    id: 'psft-production',
    name: 'PeopleSoft Production',
    description: 'Production configuration for Oracle PeopleSoft',
    platform: 'PSFT',
    scenario: 'Production',
    config: {
      platform: {
        selectedPlatform: 'PSFT',
        platformName: 'Oracle PeopleSoft ERP',
        platformVersion: '9.2',
        platformDescription: 'Oracle PeopleSoft Production',
      },
      logging: {
        level: 'warn',
        format: 'json',
        maxFileSize: 500,
        maxFiles: 20,
        retentionDays: 90,
        enableConsoleLogging: false,
        enableFileLogging: true,
      },
    },
  },
];

export const ConfigurationTemplatesPanel: React.FC = () => {
  const { config, updatePlatform } = useConfigStore();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const applyTemplate = (template: ConfigTemplate) => {
    setSelectedTemplate(template.id);
    setShowConfirm(true);
  };

  const confirmApplyTemplate = () => {
    const template = CONFIGURATION_TEMPLATES.find((t) => t.id === selectedTemplate);
    if (template) {
      // Merge template config with current config
      const mergedConfig = {
        ...config,
        ...template.config,
        platform: {
          ...config.platform,
          ...template.config.platform,
        },
      };

      // Update store with merged config
      Object.entries(mergedConfig).forEach(([key, value]) => {
        if (key === 'platform') {
          updatePlatform(value as any);
        }
      });

      setShowConfirm(false);
      setSelectedTemplate(null);
    }
  };

  const filteredTemplates = CONFIGURATION_TEMPLATES.filter(
    (t) => !config.platform?.selectedPlatform || t.platform === config.platform.selectedPlatform
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4">Configuration Templates</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="border rounded-lg p-4 hover:shadow-lg transition">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-semibold">{template.name}</h4>
                <p className="text-sm text-gray-600">{template.platform}</p>
              </div>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {template.scenario}
              </span>
            </div>

            <p className="text-sm text-gray-700 mb-4">{template.description}</p>

            <button
              onClick={() => applyTemplate(template)}
              className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Apply Template
            </button>
          </div>
        ))}
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Apply Template?</h3>
            <p className="text-gray-700 mb-6">
              This will apply the selected template configuration. Your current settings will be merged with the template.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmApplyTemplate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Info */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">About Templates</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Templates provide pre-configured settings for different scenarios</li>
          <li>• Choose between Production, Development, Testing, or Staging templates</li>
          <li>• Templates are platform-specific and can be customized after application</li>
          <li>• All templates follow security best practices for their scenario</li>
        </ul>
      </div>
    </div>
  );
};

export default ConfigurationTemplatesPanel;
